import { useCallback, useEffect, useMemo } from "react";

import { useEffectiveSummaries } from "@/modules/dashboard/settings/hooks/useEffectiveSummaries";
import { useRoles } from "@/modules/dashboard/settings/hooks/useRoles";
import { principalRolesService } from "@/modules/dashboard/settings/services/principalRolesService";
import type {
  AccessRole,
  AssignedRole,
  EffectiveSummary,
} from "@/modules/dashboard/settings/types/access.types";

import type { UserListItem } from "../types/users-api.types";

/**
 * The RBAC half of the roster: the role catalog, each visible person's assigned roles, and their effective
 * permission tally.
 *
 * **What is reused and what is not.** `useRoles`, `useEffectiveSummaries` and `principalRolesService` are
 * imported from the access screen because they are context-free and carry no dependency back into this
 * module. `useAccessUsers`, `useRoleMembers` and `AccessDataProvider` are NOT — each of them imports this
 * module's service, so importing them here would close a cycle, and `useAccessUsers` initialises a
 * module-scoped config object from that import, which is precisely the shape that fails with a TDZ
 * `undefined` rather than a loud error.
 *
 * **Cost.** The tally is `roles → policies → statements` resolved deny-first over the 84-cell catalog, per
 * person. `useEffectiveSummaries` memoises on `(assignments, catalog)` only — never on the search query — so
 * typing in the filter box recomputes nothing. That memo key is load-bearing; do not widen it.
 *
 * **Why the catalog must be complete.** Resolving an assignment against a partially loaded catalog silently
 * renders an authoritative-looking `0/84`. `useRoles` pages to exhaustion for exactly this reason, and
 * `rolesUnavailable` below is what keeps "we could not read the catalog" from being drawn as "holds
 * nothing".
 */

export interface UsersAccessState {
  roles: AccessRole[];
  rolesById: Record<string, AssignedRole[]>;
  /**
   * One principal's effective tally, or `undefined` when no tally is trustworthy yet.
   *
   * A function rather than the raw `summaries` record, and the record is deliberately NOT exposed: reading it
   * directly skips the guard below, and `summarize` returns `total: 84` unconditionally — so an unloaded or
   * unreadable catalog yields a confident, false `0/84`. That is exactly what happened when the table applied
   * the guard and the detail drawer, reading the record, did not: the row said nothing and the drawer two
   * pixels in front of it said "0/84 permisos efectivos" for someone holding three roles.
   */
  summaryFor: (userId: string) => EffectiveSummary | undefined;
  /** The catalog could not be read, so no tally is trustworthy. Never render a tally as 0 in this state. */
  rolesUnavailable: boolean;
  isLoadingRoles: boolean;
  assignRoles: (userId: string, roleIds: string[]) => Promise<AssignedRole[]>;
  removeRole: (userId: string, roleId: string) => Promise<void>;
  reloadRoles: () => void;
}

export function useUsersAccess(rows: readonly UserListItem[], enabled: boolean = true): UsersAccessState {
  const { roles, isLoading: isLoadingRoles, hasNoAccess, load } = useRoles();

  useEffect(() => {
    if (!enabled) return;
    void load().catch(() => {
      // `useRoles` rethrows anything that is not a 401/403. Swallowed here on purpose: an unreadable role
      // catalog degrades the Acceso column to "no disponible" and must not take the roster down with it.
    });
  }, [enabled, load]);

  // Memoised on `rows` so the summaries memo below keeps a stable key. Rebuilding this object every render
  // would defeat `useEffectiveSummaries` entirely and re-run the deny-first resolution on every keystroke.
  const rolesById = useMemo(() => {
    const out: Record<string, AssignedRole[]> = {};
    for (const row of rows) {
      // `include=roles` always populates the key, and `[]` is authoritative — the person holds none.
      out[row.id] = (row.roles ?? []) as AssignedRole[];
    }
    return out;
  }, [rows]);

  const summaries = useEffectiveSummaries(rolesById, roles);

  /**
   * `!enabled` is first and is NOT redundant. When the caller lacks `roles:read` the catalog is never
   * requested, so `useRoles` leaves `isLoading` at its initial `true` forever — and a check of the form
   * `!isLoading && roles.length === 0` would then evaluate to FALSE, i.e. "the catalog is fine". Every
   * principal would resolve against an empty catalog and the table would print an authoritative `0/84` next
   * to correct role chips, with the disclosure banner unable to fire.
   */
  const rolesUnavailable = !enabled || hasNoAccess || (!isLoadingRoles && roles.length === 0);
  // Disabled is a settled state, not a pending one: nothing is loading because nothing was asked for.
  const isCatalogLoading = enabled && isLoadingRoles;

  // The one place the "no tally until the catalog is loaded" rule lives, so no consumer can forget it.
  const summaryFor = useCallback(
    (userId: string) => (rolesUnavailable || isCatalogLoading ? undefined : summaries[userId]),
    [rolesUnavailable, isCatalogLoading, summaries],
  );

  const value = useMemo<UsersAccessState>(
    () => ({
      roles,
      rolesById,
      summaryFor,
      rolesUnavailable,
      isLoadingRoles: isCatalogLoading,
      assignRoles: (userId, roleIds) => principalRolesService.assignUserRoles(userId, roleIds),
      removeRole: (userId, roleId) => principalRolesService.removeUserRole(userId, roleId),
      reloadRoles: () => {
        void load().catch(() => {});
      },
    }),
    [roles, rolesById, summaryFor, rolesUnavailable, isCatalogLoading, load],
  );

  return value;
}
